import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import AsyncStorage from "@react-native-async-storage/async-storage";

interface UiPreferencesState {
  selectedDepartment: {
    id: string;
    displayText: string;
  } | null;
  selectedLocation: {
    id: string;
    displayText: string;
  } | null;
}

const LOCATION_STORAGE_KEY = "selectedLocation";

// Helper function to load saved location from storage (both web and mobile)
const loadSavedLocation = async (): Promise<{
  id: string;
  displayText: string;
} | null> => {
  try {
    // Check if running on web (has window and localStorage)
    if (typeof window !== "undefined" && window.localStorage) {
      const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
      return savedLocation ? JSON.parse(savedLocation) : null;
    }
    // Running on React Native (iOS/Android)
    else {
      const savedLocation = await AsyncStorage.getItem(LOCATION_STORAGE_KEY);
      return savedLocation ? JSON.parse(savedLocation) : null;
    }
  } catch (error) {
    console.warn("Failed to load saved location from storage:", error);
    return null;
  }
};

// Helper function to save location to storage (both web and mobile)
const saveLocationToStorage = async (
  location: { id: string; displayText: string } | null
): Promise<void> => {
  try {
    // Check if running on web (has window and localStorage)
    if (typeof window !== "undefined" && window.localStorage) {
      if (location) {
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
      } else {
        localStorage.removeItem(LOCATION_STORAGE_KEY);
      }
    }
    // Running on React Native (iOS/Android)
    else {
      if (location) {
        await AsyncStorage.setItem(
          LOCATION_STORAGE_KEY,
          JSON.stringify(location)
        );
      } else {
        await AsyncStorage.removeItem(LOCATION_STORAGE_KEY);
      }
    }
  } catch (error) {
    console.warn("Failed to save location to storage:", error);
  }
};

const initialState: UiPreferencesState = {
  selectedDepartment: null,
  selectedLocation: null, // Will be loaded asynchronously
};

const uiPreferencesSlice = createSlice({
  name: "uiPreferences",
  initialState,
  reducers: {
    setSelectedDepartment: (
      state,
      action: PayloadAction<{ id: string; displayText: string } | null>
    ) => {
      state.selectedDepartment = action.payload;
    },
    setSelectedLocation: (
      state,
      action: PayloadAction<{ id: string; displayText: string } | null>
    ) => {
      console.log("setSelectedLocation action payload:", action.payload);
      state.selectedLocation = action.payload;
      // Save to storage whenever location changes
      saveLocationToStorage(action.payload);
    },
    loadSelectedLocation: (
      state,
      action: PayloadAction<{ id: string; displayText: string } | null>
    ) => {
      state.selectedLocation = action.payload;
    },
  },
});

export const {
  setSelectedDepartment,
  setSelectedLocation,
  loadSelectedLocation,
} = uiPreferencesSlice.actions;

// Export function to initialize saved location
export const initializeSavedLocation = () => async (dispatch: any) => {
  const savedLocation = await loadSavedLocation();
  if (savedLocation) {
    dispatch(loadSelectedLocation(savedLocation));
  }
};

export default uiPreferencesSlice.reducer;
